import type { ApolloClient } from '@apollo/client';
import { from, map } from 'rxjs';
import { GetMeasuringPointsDocument, type GetMeasuringPointsQuery } from '@/graphql/types';
import { isPermission, isSwarmType } from '../../../typeGuards';
import { getPreviewMeasuringPoints } from '../../../previewContextModels';
import type { MeasuringPointModel } from '../../../elements/types';
import type { RuntimeDataServiceInput, RuntimeDataServiceOutput } from '../contracts';
import { RuntimeDataService } from '../types';
import type {
    RuntimeDataServiceRequest,
    RuntimeResultLinkDescriptorTabular,
    RuntimeTabularColumnDescriptor,
} from '../types';
import { isRecord, normalizeArrayEntries, normalizeRequiredId } from '../validationPrimitives';

export function normalizeMeasuringPointModel(
    measuringPoint: unknown,
    pathLabel: string
): MeasuringPointModel {
    if (!isRecord(measuringPoint)) {
        throw new Error(`Expected "${pathLabel}" to be a measuring-point object.`);
    }

    const id = normalizeRequiredId(measuringPoint.id, `${pathLabel}.id`);
    const { name, deviceName, swarmType, permission } = measuringPoint;

    if (typeof name !== 'string') {
        throw new Error(`Expected "${pathLabel}.name" to be a string.`);
    }

    if (deviceName !== null && deviceName !== undefined && typeof deviceName !== 'string') {
        throw new Error(`Expected "${pathLabel}.deviceName" to be a string or null.`);
    }

    if (!isSwarmType(swarmType)) {
        throw new Error(`Expected "${pathLabel}.swarmType" to be a valid SwarmType value.`);
    }

    if (!isPermission(permission)) {
        throw new Error(`Expected "${pathLabel}.permission" to be a valid Permission value.`);
    }

    return {
        id,
        name,
        deviceName: deviceName ?? null,
        swarmType,
        permission,
    };
}

function validateProjectMeasuringPointsInput(
    input: unknown
): RuntimeDataServiceInput<'project.measuring_points'> {
    if (!isRecord(input)) {
        throw new Error('Expected "project.measuring_points" input to be an object.');
    }

    return {
        projectId: normalizeRequiredId(input.projectId, 'project.measuring_points.projectId'),
    };
}

export function validateProjectMeasuringPointsResult(
    result: unknown
): RuntimeDataServiceOutput<'project.measuring_points'> {
    return normalizeArrayEntries(result, 'project.measuring_points', (entry, entryPath) =>
        normalizeMeasuringPointModel(entry, entryPath)
    );
}

function getProjectMeasuringPointsFromQuery(
    data: GetMeasuringPointsQuery,
    projectId: string
): MeasuringPointModel[] {
    if (!data.measuringPoints) {
        throw new Error(
            'Missing measuringPoints payload while resolving project measuring points.'
        );
    }

    const projectScopedData: GetMeasuringPointsQuery = {
        ...data,
        measuringPoints: data.measuringPoints.filter(
            (entry) => entry && entry.project === projectId
        ),
    };

    return getPreviewMeasuringPoints(projectScopedData).map((entry) => ({
        id: entry.id,
        name: entry.name,
        deviceName: entry.deviceName,
        swarmType: entry.swarmType,
        permission: entry.permission,
    }));
}

function createProjectMeasuringPointColumns() {
    return [
        {
            kind: 'text',
            key: 'id',
            label: gettext('ID'),
            defaultAlignment: 'left',
            resolveCell: (entry: MeasuringPointModel) => entry.id,
        },
        {
            kind: 'text',
            key: 'name',
            label: gettext('NAME'),
            defaultAlignment: 'left',
            resolveCell: (entry: MeasuringPointModel) => entry.name,
        },
        {
            kind: 'text',
            key: 'deviceName',
            label: gettext('DEVICE_NAME'),
            defaultAlignment: 'left',
            resolveCell: (entry: MeasuringPointModel) => entry.deviceName,
        },
        {
            kind: 'text',
            key: 'swarmType',
            label: gettext('SWARM_TYPE'),
            defaultAlignment: 'left',
            resolveCell: (entry: MeasuringPointModel) => entry.swarmType,
        },
        {
            kind: 'text',
            key: 'permission',
            label: gettext('PERMISSION'),
            defaultAlignment: 'left',
            resolveCell: (entry: MeasuringPointModel) => entry.permission,
        },
    ] as const satisfies readonly RuntimeTabularColumnDescriptor<MeasuringPointModel>[];
}

export class ProjectMeasuringPointsRuntimeDataService extends RuntimeDataService<'project.measuring_points'> {
    static readonly serviceId = 'project.measuring_points' as const;

    static get serviceMetadata() {
        return {
            label: gettext('DATA_SERVICE_PROJECT_MEASURING_POINTS_LABEL'),
            description: gettext('DATA_SERVICE_PROJECT_MEASURING_POINTS_DESCRIPTION'),
        } as const;
    }

    static get serviceResultLinks() {
        return [
            {
                key: 'measuring_points',
                label: gettext('DATA_SERVICE_PROJECT_MEASURING_POINTS_LINK_LABEL'),
                description: gettext('DATA_SERVICE_PROJECT_MEASURING_POINTS_LINK_DESCRIPTION'),
                supportedContextKinds: ['project'],
                supportedSwarmTypes: null,
                path: '',
                valueKind: 'tabular',
                argPolicy: 'optional',
                exposeInInsertionPresets: true,
                columns: createProjectMeasuringPointColumns(),
                parseTableRows: validateProjectMeasuringPointsResult,
                buildInput: ({ context, arg }) => {
                    if (arg !== null) {
                        return { projectId: arg };
                    }

                    if (!context || context.kind !== 'project') {
                        throw new Error(
                            'Project measuring points binding requires project context.'
                        );
                    }

                    return { projectId: context.project.id };
                },
            },
        ] as const satisfies readonly RuntimeResultLinkDescriptorTabular<
            'project.measuring_points',
            MeasuringPointModel
        >[];
    }

    constructor(private readonly client: ApolloClient<object>) {
        super();
    }

    validateArgs = validateProjectMeasuringPointsInput;

    validateResult = validateProjectMeasuringPointsResult;

    createResult$ = ({ input }: RuntimeDataServiceRequest<'project.measuring_points'>) =>
        from(
            this.client.query<GetMeasuringPointsQuery>({
                query: GetMeasuringPointsDocument,
                fetchPolicy: 'cache-first',
            })
        ).pipe(
            map(({ data }) => {
                if (!data) {
                    throw new Error(
                        'Missing measuring points query result for runtime data services.'
                    );
                }

                return getProjectMeasuringPointsFromQuery(data, input.projectId);
            })
        );
}

export function createProjectMeasuringPointsService(
    client: ApolloClient<object>
): ProjectMeasuringPointsRuntimeDataService {
    return new ProjectMeasuringPointsRuntimeDataService(client);
}
